/**
 * Daily Ops tab — NK-CAT pipeline kill switch, budget controls, and the
 * daily report card.
 *
 * Backs the admin's "I want to know what happened each day" view:
 *   - Kill switch (instant on/off, no redeploy)
 *   - Daily spend bar chart, stacked by API (Perplexity / Tavily / nano)
 *   - Today's new vs refreshed vs weeded-out counts + highlights
 *   - URL liveness posture (verified ratio, stale check count)
 *
 * Sprint: NK-CAT budget + daily ops dashboard (2026-05-13)
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity, AlertTriangle, CheckCircle2, DollarSign, Link2,
  Power, RefreshCw, Trash2, TrendingUp, XCircle,
} from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Legend, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { adminFetch } from '../../../utils/adminApi';


// ────────────────────────────────────────────────────────────────────────
// Types — match the admin_nkcat.py response shapes
// ────────────────────────────────────────────────────────────────────────

interface SettingsState {
  enabled: boolean;
  daily_budget_usd: number;
  daily_hard_stop_usd: number;
  spent_today_usd: number;
  over_soft_limit: boolean;
  over_hard_stop: boolean;
  can_proceed: boolean;
}

interface DailyReport {
  date: string;
  cost: {
    total_usd: number;
    perplexity_usd: number;
    tavily_usd: number;
    nano_usd: number;
    perplexity_calls: number;
    tavily_calls: number;
    nano_calls: number;
  };
  runs: {
    total: number;
    completed: number;
    failed: number;
    skipped_by_budget_gate: number;
  };
  sources: {
    new: number;
    refreshed: number;
    rejected: number;
    superseded: number;
  };
  highlights: Array<{
    id: string;
    title: string;
    category: string | null;
    subcategory: string | null;
    knowledge_type: string | null;
    url_count: number;
  }>;
  by_category: Array<{
    category: string;
    new: number;
    refreshed: number;
  }>;
}

interface SpendChartResponse {
  days: number;
  start_date: string;
  end_date: string;
  budget_line_usd: number;
  hard_stop_line_usd: number;
  window_total_usd: number;
  window_avg_usd_per_day: number;
  series: Array<{
    day: string;
    perplexity_usd: number;
    tavily_usd: number;
    nano_usd: number;
    total_usd: number;
    runs: number;
  }>;
}

interface HealthResponse {
  url_health: {
    total_sources: number;
    verified: number;
    unverified: number;
    stale_or_unchecked: number;
    verified_pct: number;
    oldest_check_at: string | null;
    newest_check_at: string | null;
  };
  last_run: {
    started_at: string;
    status: string;
    agent_type: string;
    cost_usd: number;
  } | null;
  corpus_mix: Array<{ knowledge_type: string; count: number }>;
}


// ────────────────────────────────────────────────────────────────────────
// Card helper — matches the styling of OverviewTab.tsx Kpi
// ────────────────────────────────────────────────────────────────────────

function Card({
  title,
  icon: Icon,
  tone = 'neutral',
  children,
  action,
}: {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  tone?: 'neutral' | 'good' | 'warn' | 'danger';
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  const tones: Record<string, string> = {
    neutral: 'border-gray-200 bg-white',
    good: 'border-emerald-200 bg-emerald-50',
    warn: 'border-amber-200 bg-amber-50',
    danger: 'border-red-200 bg-red-50',
  };
  return (
    <div className={`rounded-lg border p-4 shadow-sm ${tones[tone]}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-gray-500" />}
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {title}
          </span>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Settings + kill switch
// ────────────────────────────────────────────────────────────────────────

function SettingsPanel({ settings }: { settings: SettingsState }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState(String(settings.daily_budget_usd));
  const [hardStopDraft, setHardStopDraft] = useState(String(settings.daily_hard_stop_usd));

  const updateSettings = useMutation({
    mutationFn: async (body: Partial<SettingsState> & { notes?: string }) => {
      return adminFetch<SettingsState>('/api/admin/nkcat/settings', {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nkcat-settings'] });
      qc.invalidateQueries({ queryKey: ['nkcat-daily-report'] });
      qc.invalidateQueries({ queryKey: ['nkcat-spend-chart'] });
      setEditing(false);
    },
  });

  const onToggle = () => {
    updateSettings.mutate({ enabled: !settings.enabled });
  };

  const onSaveBudget = () => {
    const budget = parseFloat(budgetDraft);
    const hardStop = parseFloat(hardStopDraft);
    if (Number.isNaN(budget) || Number.isNaN(hardStop)) {
      alert('Budgets must be numbers');
      return;
    }
    if (hardStop < budget) {
      alert('Hard stop must be ≥ daily budget');
      return;
    }
    updateSettings.mutate({
      daily_budget_usd: budget,
      daily_hard_stop_usd: hardStop,
    });
  };

  const tone: 'good' | 'warn' | 'danger' | 'neutral' = !settings.enabled
    ? 'neutral'
    : settings.over_hard_stop
    ? 'danger'
    : settings.over_soft_limit
    ? 'warn'
    : 'good';

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Kill switch */}
      <Card title="Pipeline" icon={Power} tone={tone}>
        <div className="flex items-center justify-between">
          <div>
            <div className={`text-2xl font-semibold ${settings.enabled ? 'text-emerald-700' : 'text-gray-500'}`}>
              {settings.enabled ? 'ENABLED' : 'DISABLED'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {settings.can_proceed
                ? 'Pipeline can spend'
                : !settings.enabled
                ? 'Toggle on to allow runs'
                : 'Hard stop reached for today'}
            </div>
          </div>
          <button
            onClick={onToggle}
            disabled={updateSettings.isPending}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.enabled ? 'bg-emerald-500' : 'bg-gray-300'
            } ${updateSettings.isPending ? 'opacity-50' : ''}`}
            aria-label="Toggle pipeline"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </Card>

      {/* Today's spend gauge */}
      <Card title="Today's spend" icon={DollarSign} tone={tone}>
        <div className="text-2xl font-semibold text-gray-900">
          ${settings.spent_today_usd.toFixed(2)}
          <span className="text-sm font-normal text-gray-500">
            {' '}/ ${settings.daily_budget_usd.toFixed(2)}
          </span>
        </div>
        <div className="mt-2 h-2 w-full rounded-full bg-gray-200 overflow-hidden">
          <div
            className={`h-full transition-all ${
              settings.over_hard_stop ? 'bg-red-500' : settings.over_soft_limit ? 'bg-amber-500' : 'bg-emerald-500'
            }`}
            style={{
              width: `${Math.min(100, (settings.spent_today_usd / Math.max(settings.daily_budget_usd, 0.01)) * 100)}%`,
            }}
          />
        </div>
        <div className="mt-1 text-xs text-gray-500">
          Hard stop at ${settings.daily_hard_stop_usd.toFixed(2)}
        </div>
      </Card>

      {/* Budget editor */}
      <Card
        title="Budget"
        icon={TrendingUp}
        action={
          <button
            onClick={() => setEditing(!editing)}
            className="text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            {editing ? 'Cancel' : 'Edit'}
          </button>
        }
      >
        {editing ? (
          <div className="space-y-2">
            <label className="block">
              <span className="text-xs text-gray-600">Daily budget (USD)</span>
              <input
                type="number"
                step="0.50"
                min="0"
                max="100"
                value={budgetDraft}
                onChange={(e) => setBudgetDraft(e.target.value)}
                className="mt-1 w-full rounded border-gray-300 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs text-gray-600">Hard stop (USD)</span>
              <input
                type="number"
                step="0.50"
                min="0"
                max="100"
                value={hardStopDraft}
                onChange={(e) => setHardStopDraft(e.target.value)}
                className="mt-1 w-full rounded border-gray-300 text-sm"
              />
            </label>
            <button
              onClick={onSaveBudget}
              disabled={updateSettings.isPending}
              className="w-full rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {updateSettings.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : (
          <>
            <div className="text-2xl font-semibold text-gray-900">
              ${settings.daily_budget_usd.toFixed(2)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Hard stop: ${settings.daily_hard_stop_usd.toFixed(2)}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// 14-day stacked spend chart
// ────────────────────────────────────────────────────────────────────────

const TOOLTIP_STYLE = {
  backgroundColor: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  padding: '8px 12px',
  fontSize: '12px',
};

function SpendChart({ data }: { data: SpendChartResponse }) {
  // Format X axis labels as MMM-DD ('05-13' style)
  const formatted = data.series.map((d) => ({
    ...d,
    day_short: d.day.slice(5),
  }));

  return (
    <Card title={`Daily spend — last ${data.days} days`} icon={Activity}>
      <div className="mb-3 flex items-center gap-6 text-sm">
        <div>
          <span className="text-gray-500">Window total:</span>{' '}
          <span className="font-semibold">${data.window_total_usd.toFixed(2)}</span>
        </div>
        <div>
          <span className="text-gray-500">Avg / day:</span>{' '}
          <span className="font-semibold">${data.window_avg_usd_per_day.toFixed(2)}</span>
        </div>
        <div>
          <span className="text-gray-500">Budget line:</span>{' '}
          <span className="font-semibold text-emerald-700">${data.budget_line_usd.toFixed(2)}</span>
        </div>
        <div>
          <span className="text-gray-500">Hard stop:</span>{' '}
          <span className="font-semibold text-red-700">${data.hard_stop_line_usd.toFixed(2)}</span>
        </div>
      </div>
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <BarChart data={formatted} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="day_short" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(v) => `$${v.toFixed(2)}`} tick={{ fontSize: 11 }} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(value: number, name: string) => [`$${value.toFixed(4)}`, name]}
              labelFormatter={(label) => `Day ${label}`}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine
              y={data.budget_line_usd}
              stroke="#10b981"
              strokeDasharray="4 4"
              label={{ value: 'Budget', position: 'insideTopRight', fill: '#10b981', fontSize: 10 }}
            />
            <ReferenceLine
              y={data.hard_stop_line_usd}
              stroke="#ef4444"
              strokeDasharray="4 4"
              label={{ value: 'Hard stop', position: 'insideTopRight', fill: '#ef4444', fontSize: 10 }}
            />
            <Bar dataKey="perplexity_usd" stackId="cost" fill="#6366f1" name="Perplexity" />
            <Bar dataKey="tavily_usd" stackId="cost" fill="#06b6d4" name="Tavily" />
            <Bar dataKey="nano_usd" stackId="cost" fill="#9ca3af" name="gpt-5-nano" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Today's report card
// ────────────────────────────────────────────────────────────────────────

function ReportCardRow({ report }: { report: DailyReport }) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card title="New sources today" icon={CheckCircle2} tone="good">
          <div className="text-3xl font-semibold text-emerald-700">{report.sources.new}</div>
          <div className="text-xs text-gray-500 mt-1">
            Top: {report.by_category[0]?.category ?? 'none'}
          </div>
        </Card>
        <Card title="Refreshed today" icon={RefreshCw} tone="neutral">
          <div className="text-3xl font-semibold text-gray-800">{report.sources.refreshed}</div>
          <div className="text-xs text-gray-500 mt-1">existing entries overwritten</div>
        </Card>
        <Card title="Rejected today" icon={XCircle} tone={report.sources.rejected > 0 ? 'warn' : 'neutral'}>
          <div className="text-3xl font-semibold text-amber-700">{report.sources.rejected}</div>
          <div className="text-xs text-gray-500 mt-1">quality &lt; threshold</div>
        </Card>
        <Card title="Superseded today" icon={Trash2} tone="neutral">
          <div className="text-3xl font-semibold text-gray-700">{report.sources.superseded}</div>
          <div className="text-xs text-gray-500 mt-1">replaced by newer entries</div>
        </Card>
      </div>

      {/* Highlights */}
      <Card title={`Highlights — ${report.date}`} icon={TrendingUp}>
        {report.highlights.length === 0 ? (
          <div className="text-sm text-gray-500 italic">
            No new sources today.
          </div>
        ) : (
          <ul className="space-y-2">
            {report.highlights.map((h) => (
              <li
                key={h.id}
                className="flex items-start gap-2 text-sm py-1 border-b border-gray-100 last:border-b-0"
              >
                <span className="text-gray-400 mt-0.5">•</span>
                <div className="flex-1">
                  <div className="text-gray-900">{h.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5 flex gap-2">
                    {h.category && <span>{h.category}</span>}
                    {h.subcategory && <span>· {h.subcategory}</span>}
                    {h.knowledge_type && (
                      <span className="rounded bg-gray-100 px-1.5 py-0.5">
                        {h.knowledge_type}
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-gray-400">
                      <Link2 className="h-3 w-3" />
                      {h.url_count} URL{h.url_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Cost + runs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Today's API calls" icon={Activity}>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-xs text-gray-500">Perplexity</div>
              <div className="text-lg font-semibold text-indigo-600">{report.cost.perplexity_calls}</div>
              <div className="text-xs text-gray-400">${report.cost.perplexity_usd.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Tavily</div>
              <div className="text-lg font-semibold text-cyan-600">{report.cost.tavily_calls}</div>
              <div className="text-xs text-gray-400">${report.cost.tavily_usd.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">gpt-5-nano</div>
              <div className="text-lg font-semibold text-gray-700">{report.cost.nano_calls}</div>
              <div className="text-xs text-gray-400">${report.cost.nano_usd.toFixed(2)}</div>
            </div>
          </div>
        </Card>
        <Card title="Today's runs" icon={RefreshCw}>
          <div className="grid grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-xs text-gray-500">Total</div>
              <div className="text-lg font-semibold text-gray-800">{report.runs.total}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Done</div>
              <div className="text-lg font-semibold text-emerald-700">{report.runs.completed}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Failed</div>
              <div className="text-lg font-semibold text-red-600">{report.runs.failed}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Skipped</div>
              <div className="text-lg font-semibold text-amber-600">{report.runs.skipped_by_budget_gate}</div>
            </div>
          </div>
        </Card>
      </div>

      {/* By category */}
      {report.by_category.length > 0 && (
        <Card title="By category — new + refreshed">
          <ul className="space-y-1 text-sm">
            {report.by_category.map((c) => (
              <li key={c.category} className="flex justify-between border-b border-gray-100 py-1.5 last:border-b-0">
                <span className="text-gray-700">{c.category}</span>
                <span className="text-gray-500">
                  <span className="text-emerald-700 font-medium">+{c.new}</span>
                  {' '}new ·{' '}
                  <span className="text-blue-700 font-medium">{c.refreshed}</span>
                  {' '}refreshed
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Pipeline health (URL validity, last run, corpus mix)
// ────────────────────────────────────────────────────────────────────────

function HealthPanel({ health }: { health: HealthResponse }) {
  const verifiedTone =
    health.url_health.verified_pct >= 95 ? 'good' :
    health.url_health.verified_pct >= 80 ? 'neutral' : 'warn';

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card title="URL health" icon={Link2} tone={verifiedTone}>
        <div className="text-2xl font-semibold text-gray-900">
          {health.url_health.verified_pct.toFixed(1)}%
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {health.url_health.verified.toLocaleString()} verified of {' '}
          {health.url_health.total_sources.toLocaleString()} total
        </div>
        <div className="text-xs text-amber-700 mt-2">
          {health.url_health.stale_or_unchecked.toLocaleString()} stale or unchecked (&gt; 30 days)
        </div>
      </Card>

      <Card title="Last pipeline run" icon={Activity}>
        {health.last_run ? (
          <>
            <div className="text-sm font-medium text-gray-900">
              {health.last_run.agent_type}{' '}
              <span
                className={`ml-1 inline-block rounded px-1.5 py-0.5 text-xs ${
                  health.last_run.status === 'completed'
                    ? 'bg-emerald-100 text-emerald-700'
                    : health.last_run.status === 'failed'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-amber-100 text-amber-700'
                }`}
              >
                {health.last_run.status}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {new Date(health.last_run.started_at).toLocaleString()}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Cost: ${health.last_run.cost_usd.toFixed(4)}
            </div>
          </>
        ) : (
          <div className="text-sm text-gray-500 italic">No runs yet</div>
        )}
      </Card>

      <Card title="Corpus mix">
        <ul className="space-y-1 text-sm">
          {health.corpus_mix.map((m) => (
            <li key={m.knowledge_type} className="flex justify-between">
              <span className="text-gray-700">{m.knowledge_type}</span>
              <span className="text-gray-500 font-medium">
                {m.count.toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Main tab — composes the panels
// ────────────────────────────────────────────────────────────────────────

export function DailyOpsTab() {
  const settingsQ = useQuery({
    queryKey: ['nkcat-settings'],
    queryFn: () => adminFetch<SettingsState>('/api/admin/nkcat/settings'),
    refetchInterval: 30_000,
  });

  const reportQ = useQuery({
    queryKey: ['nkcat-daily-report'],
    queryFn: () => adminFetch<DailyReport>('/api/admin/nkcat/daily-report'),
    refetchInterval: 60_000,
  });

  const chartQ = useQuery({
    queryKey: ['nkcat-spend-chart', 14],
    queryFn: () => adminFetch<SpendChartResponse>('/api/admin/nkcat/spend-chart?days=14'),
    refetchInterval: 60_000,
  });

  const healthQ = useQuery({
    queryKey: ['nkcat-health'],
    queryFn: () => adminFetch<HealthResponse>('/api/admin/nkcat/health'),
    refetchInterval: 60_000,
  });

  if (settingsQ.isError || reportQ.isError || chartQ.isError || healthQ.isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <div className="flex items-center gap-2 font-medium">
          <AlertTriangle className="h-4 w-4" />
          Failed to load NK-CAT Daily Ops data.
        </div>
        <div className="mt-2 text-xs">
          Confirm migration 1485 has run and the admin_nkcat router is
          registered. Check CloudWatch for endpoint errors.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {settingsQ.data && <SettingsPanel settings={settingsQ.data} />}
      {chartQ.data && <SpendChart data={chartQ.data} />}
      {reportQ.data && <ReportCardRow report={reportQ.data} />}
      {healthQ.data && <HealthPanel health={healthQ.data} />}
    </div>
  );
}
